import { useState, useMemo } from 'react';
import { PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import InvoicePDF from './InvoicePDF';
import { sendClientInvoice } from '../services/api';

function fmtCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(val) || 0);
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function substitute(template, vars) {
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{{${k}}}`).join(v ?? ''),
    template || ''
  );
}

function buildTemplateVars({ invoice, lineItems, client, billingSettings }) {
  const subtotal = lineItems.reduce((sum, li) => sum + Number(li.line_total || 0), 0);
  const paid = Number(invoice.amount_paid || 0);
  const balance = subtotal - paid;
  return {
    client_name: client.name,
    facility_name: client.facility_name,
    invoice_number: invoice.invoice_number,
    invoice_date: fmtDate(invoice.invoice_date),
    due_date: fmtDate(invoice.due_date),
    period_start: fmtDate(invoice.period_start),
    period_end: fmtDate(invoice.period_end),
    total: fmtCurrency(subtotal),
    balance_due: fmtCurrency(balance),
    biller_name: billingSettings?.biller_name || '',
    biller_phone: billingSettings?.biller_phone || '',
    biller_email: billingSettings?.biller_email || '',
  };
}

// ─── Preview Modal ─────────────────────────────────────────────────────────────

export function InvoicePreviewModal({ invoice, lineItems, client, billingSettings, onClose, onSent }) {
  const [showSend, setShowSend] = useState(false);

  const doc = useMemo(
    () => <InvoicePDF invoice={invoice} lineItems={lineItems} client={client} billingSettings={billingSettings} />,
    [invoice, lineItems, client, billingSettings]
  );

  const isVoid = invoice.status === 'void';

  return (
    <>
      <div className="invoice-preview-overlay">
        <div className="invoice-preview-modal">
          <div className="invoice-preview-header">
            <div>
              <h2>Invoice Preview</h2>
              <p>{invoice.invoice_number} • {client.name}</p>
            </div>
            <button className="invoice-preview-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="invoice-preview-body">
            <PDFViewer width="100%" height="100%" showToolbar={false} style={{ border: 'none' }}>
              {doc}
            </PDFViewer>
          </div>

          <div className="invoice-preview-actions">
            <PDFDownloadLink
              document={doc}
              fileName={`${invoice.invoice_number}.pdf`}
              className="invoice-preview-btn secondary"
            >
              {({ loading }) => (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {loading ? 'Preparing...' : 'Download PDF'}
                </>
              )}
            </PDFDownloadLink>

            <button
              className="invoice-preview-btn primary"
              onClick={() => setShowSend(true)}
              disabled={isVoid || lineItems.length === 0}
              title={
                isVoid ? 'Cannot send a voided invoice'
                  : lineItems.length === 0 ? 'Add line items before sending'
                  : invoice.status === 'draft' ? 'Send to client (this will lock the invoice from edits)'
                  : 'Re-send to client'
              }
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              {invoice.status === 'draft' ? 'Send to Client' : 'Re-send'}
            </button>
          </div>
        </div>
      </div>

      {showSend && (
        <SendInvoiceModal
          invoice={invoice}
          lineItems={lineItems}
          client={client}
          billingSettings={billingSettings}
          onClose={() => setShowSend(false)}
          onSent={(updated) => {
            setShowSend(false);
            onSent?.(updated);
          }}
        />
      )}
    </>
  );
}

// ─── Send Modal ────────────────────────────────────────────────────────────────

export function SendInvoiceModal({ invoice, lineItems, client, billingSettings, onClose, onSent }) {
  const vars = useMemo(
    () => buildTemplateVars({ invoice, lineItems, client, billingSettings }),
    [invoice, lineItems, client, billingSettings]
  );

  const [to, setTo] = useState(client.contact_email || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(substitute(billingSettings?.default_email_subject || '', vars));
  const [body, setBody] = useState(substitute(billingSettings?.default_email_body || '', vars));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!to.trim()) { setError('Recipient email is required.'); return; }
    if (!subject.trim()) { setError('Subject is required.'); return; }

    setSending(true);
    setError(null);

    try {
      // Render PDF to a blob, then to base64 (strip the data: prefix).
      const blob = await pdf(
        <InvoicePDF invoice={invoice} lineItems={lineItems} client={client} billingSettings={billingSettings} />
      ).toBlob();

      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result || '';
          const idx = String(result).indexOf('base64,');
          resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const toList = to.split(',').map(s => s.trim()).filter(Boolean);
      const ccList = cc.split(',').map(s => s.trim()).filter(Boolean);

      const res = await sendClientInvoice(client.client_id, invoice.invoice_id, {
        to: toList,
        cc: ccList.length > 0 ? ccList : undefined,
        subject: subject.trim(),
        body,
        pdf_base64: base64,
        pdf_filename: `${invoice.invoice_number}.pdf`,
      });

      onSent?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send invoice.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal send-invoice-modal">
        <div className="admin-modal-header">
          <div className="admin-modal-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </div>
          <div className="admin-modal-header-text">
            <h2>Send Invoice</h2>
            <p>{invoice.invoice_number} will be attached as a PDF</p>
          </div>
        </div>

        {error && (
          <div className="admin-modal-alert error" style={{ marginBottom: 18 }}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {invoice.status === 'draft' && (
          <div className="admin-modal-alert warning" style={{ marginBottom: 18, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Sending will lock this invoice — line items cannot be edited after send. Void and reissue to make corrections.</span>
          </div>
        )}

        <form onSubmit={handleSend} className="admin-modal-form">
          <div className="admin-modal-field">
            <label>To <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="text"
              className="admin-modal-input"
              placeholder="client@example.com (comma-separate for multiple)"
              value={to}
              onChange={e => setTo(e.target.value)}
              autoFocus
            />
          </div>

          <div className="admin-modal-field">
            <label>CC <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <input
              type="text"
              className="admin-modal-input"
              placeholder="cc@example.com"
              value={cc}
              onChange={e => setCc(e.target.value)}
            />
          </div>

          <div className="admin-modal-field">
            <label>Subject <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="text"
              className="admin-modal-input"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </div>

          <div className="admin-modal-field">
            <label>Message</label>
            <textarea
              className="admin-modal-input"
              rows={10}
              style={{ fontFamily: 'inherit', resize: 'vertical' }}
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>

          <div className="send-invoice-attachment">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            <span>{invoice.invoice_number}.pdf</span>
          </div>

          <div className="admin-modal-actions">
            <button type="button" className="admin-modal-btn-cancel" onClick={onClose} disabled={sending}>Cancel</button>
            <button type="submit" className="admin-modal-btn-submit" disabled={sending}>
              {sending && <span className="admin-modal-spinner" />}
              {sending ? 'Sending...' : 'Send Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
